import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Check, X, ChevronLeft, Send, Eye, MessageCircle, Trash2 } from "lucide-react";
import { StatusIcon } from "@/components/StatusIcon";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under Review" },
  { value: "more_info_requested", label: "More Info Requested" },
  { value: "approved", label: "Approved" },
  { value: "active", label: "Active (Signed)" },
  { value: "rejected", label: "Rejected" },
  { value: "draft", label: "Draft" },
];

const STATUS_PILL = {
  draft: { label: "Draft", color: "text-[var(--bloom-ink-muted)]" },
  submitted: { label: "Submitted", color: "text-[var(--bloom-ink)]" },
  under_review: { label: "Under Review", color: "text-[var(--bloom-warning)]" },
  more_info_requested: { label: "More Info", color: "text-[var(--bloom-warning)]" },
  approved: { label: "Approved", color: "text-[var(--bloom-brand)]" },
  active: { label: "Active", color: "text-[var(--bloom-brand)]" },
  rejected: { label: "Rejected", color: "text-[var(--bloom-accent)]" },
};

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notStarted, setNotStarted] = useState(0);

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/applications${status ? `?status=${status}` : ""}`);
      setItems(data.applications);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  useEffect(() => {
    api.get("/admin/growers").then(({ data }) => {
      const n = (data.growers || []).filter((g) => ["registered", "reading", "ready_to_apply"].includes(g.stage)).length;
      setNotStarted(n);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (appId) => {
    setSelected(appId);
    setDetail(null);
    const { data } = await api.get(`/admin/applications/${appId}`);
    setDetail(data);
  };

  const afterDecision = (updated) => {
    setDetail((d) => d ? { ...d, application: updated } : d);
    setItems((arr) => arr.map((it) => it.application.id === updated.id ? { ...it, application: updated } : it));
  };

  const counts = items.reduce((acc, it) => {
    acc[it.application.status] = (acc[it.application.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="mt-16" data-testid="admin-applications">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="bloom-label mb-2">Stage Three Pipeline</div>
          <h2 className="font-serif text-3xl">Applications</h2>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bloom-input max-w-xs" data-testid="admin-app-filter">
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {notStarted > 0 && (
        <div className="mb-6 border border-[var(--bloom-border)] bg-[var(--bloom-surface)] px-4 py-3 font-serif italic text-sm text-[var(--bloom-ink-soft)]" data-testid="admin-app-pipeline-note">
          Only growers who have <strong>started</strong> the Stage 3 application form appear here.
          {" "}{notStarted} registered grower{notStarted !== 1 ? "s" : ""} {notStarted !== 1 ? "haven't" : "hasn't"} begun yet — you'll find them in the <strong>Growers</strong> tab.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-10">
        <Mini label="Submitted" value={counts.submitted || 0} />
        <Mini label="Under Review" value={counts.under_review || 0} />
        <Mini label="More Info" value={counts.more_info_requested || 0} />
        <Mini label="Approved" value={counts.approved || 0} accent />
        <Mini label="Active" value={counts.active || 0} accent />
        <Mini label="Rejected" value={counts.rejected || 0} />
        <Mini label="Drafts" value={counts.draft || 0} />
      </div>

      {loading ? (
        <div className="text-center text-[var(--bloom-ink-muted)] py-24 font-serif italic">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-[var(--bloom-ink-muted)] py-24 font-serif italic" data-testid="admin-app-empty">
          No applications {filter ? `with status "${filter}"` : "yet"}.
        </div>
      ) : (
        <div className="border border-[var(--bloom-border)] overflow-hidden" data-testid="admin-applications-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bloom-surface)] text-left">
              <tr>
                <Th>Applicant</Th>
                <Th>Region</Th>
                <Th>Status</Th>
                <Th>Score</Th>
                <Th>Submitted</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const a = it.application;
                const u = it.user;
                const pill = STATUS_PILL[a.status] || { label: a.status, color: "" };
                return (
                  <tr
                    key={a.id}
                    onClick={() => openDetail(a.id)}
                    className="border-t border-[var(--bloom-border)] cursor-pointer hover:bg-[var(--bloom-surface)]"
                    data-testid={`admin-app-row-${a.id}`}
                  >
                    <Td>
                      <div className="font-serif text-base">{u?.name || "·"}</div>
                      <div className="text-xs text-[var(--bloom-ink-muted)]">{u?.email}</div>
                      {a.data?.farm_name && <div className="text-xs italic text-[var(--bloom-ink-muted)]">{a.data.farm_name}</div>}
                    </Td>
                    <Td>{a.data?.region || u?.region || <em className="text-[var(--bloom-ink-muted)]"></em>}</Td>
                    <Td>
                      <span className={`inline-flex items-center gap-2 font-serif text-sm ${pill.color}`} style={{ textTransform: "capitalize" }}>
                        <StatusIcon name={`application.${a.status}`} size={18} />
                        {pill.label}
                      </span>
                    </Td>
                    <Td>{a.score ? <span className="font-serif text-base">{a.score.score}<span className="text-xs text-[var(--bloom-ink-muted)]">/100</span></span> : <span className="text-xs text-[var(--bloom-ink-muted)]"></span>}</Td>
                    <Td>{a.submitted_at ? <span className="text-xs">{new Date(a.submitted_at).toLocaleDateString()}</span> : <span className="text-xs text-[var(--bloom-ink-muted)]"></span>}</Td>
                    <Td><Eye size={16} className="text-[var(--bloom-ink-muted)]" /></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="admin-app-overlay">
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-[720px] bg-[var(--bloom-bg)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="bloom-btn bloom-btn-ghost m-4" data-testid="admin-app-close">
              <ChevronLeft size={16} /> Back
            </button>
            {detail ? (
              <ApplicationDetail
                detail={detail}
                onDecision={afterDecision}
                onDeleted={(id) => {
                  setItems((arr) => arr.filter((it) => it.application.id !== id));
                  setSelected(null);
                  setDetail(null);
                }}
              />
            ) : (
              <div className="p-8 font-serif italic">Loading…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationDetail({ detail, onDecision, onDeleted }) {
  const { application: a, user: u } = detail;
  const [notes, setNotes] = useState(a.review_notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const decide = async (status) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/admin/applications/${a.id}/decision`, { status, notes });
      onDecision(data.application);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Permanently delete this application for ${u?.name}? The grower account will be kept so they can re-apply, but all application data and signatures will be lost. This cannot be undone.`)) return;
    setDelBusy(true);
    try {
      await api.delete(`/admin/applications/${a.id}`);
      onDeleted?.(a.id);
    } finally {
      setDelBusy(false);
    }
  };

  const d = a.data || {};
  const photos = d.photos || [];
  const refs = d.references || [];

  return (
    <div className="p-8 md:p-10" data-testid="admin-app-detail">
      <div className="bloom-divider mb-4">✦ Founding Grower Application ✦</div>
      <h2 className="font-serif text-3xl mb-1">{u?.name}</h2>
      <p className="text-sm text-[var(--bloom-ink-soft)]">{u?.email}{u?.farm_name && <> · <em>{u.farm_name}</em></>}</p>

      {a.score && (
        <div className="border border-[var(--bloom-border)] p-5 mt-6" data-testid="app-detail-score">
          <div className="flex items-baseline justify-between mb-3">
            <div className="bloom-label">Eligibility Score</div>
            <div className="font-serif text-3xl text-[var(--bloom-brand)]">{a.score.score}<span className="text-sm text-[var(--bloom-ink-muted)]">/100</span></div>
          </div>
          <div className="grid grid-cols-5 gap-2 text-[10px] tracking-[0.15em] uppercase">
            {Object.entries(a.score.breakdown).filter(([k]) => k !== "total").map(([k, v]) => (
              <div key={k}>
                <div className="text-[var(--bloom-ink-muted)]">{k.replace(/_/g, " ")}</div>
                <div className="font-serif text-base text-[var(--bloom-ink)] normal-case mt-1" style={{ letterSpacing: 0 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DetailSection title="About">
        <Row label="Phone" v={d.phone} />
        <Row label="Address" v={[d.address_line, d.town_city, d.region, d.postcode].filter(Boolean).join(", ")} />
        <Row label="Ownership" v={d.ownership} />
      </DetailSection>

      <DetailSection title="Land & Experience">
        <Row label="Growing area" v={d.growing_area_sqm ? `${d.growing_area_sqm} sqm` : ""} />
        <Row label="Years growing" v={d.years_growing} />
        <Row label="Dahlia years" v={d.dahlia_years} />
        <Row label="Crops" v={(d.crops_grown || []).join(", ")} />
        <Row label="Current outlets" v={(d.current_outlets || []).join(", ")} />
        <Row label="Scale" v={d.scale_description} multiline />
      </DetailSection>

      <DetailSection title="Logistics & Plan">
        <Row label="Refrigeration" v={d.has_refrigeration ? "Yes" : "No"} />
        <Row label="Packing space" v={d.has_packing_space ? "Yes" : "No"} />
        <Row label="Vehicle" v={d.has_reliable_vehicle ? "Yes" : "No"} />
        <Row label="Delivery" v={d.can_deliver_locally ? `Yes · ${d.delivery_radius_km}km` : "No"} />
        <Row label="Target subscribers" v={d.target_subscribers} />
        <Row label="Weekly capacity" v={d.weekly_capacity_boxes ? `${d.weekly_capacity_boxes} boxes` : ""} />
        <Row label="Season" v={[d.season_start_month, d.season_end_month].filter(Boolean).join("·")} />
      </DetailSection>

      <DetailSection title="References">
        {refs.length === 0 ? (
          <div className="text-xs italic text-[var(--bloom-ink-muted)]">None provided</div>
        ) : refs.map((r, i) => (
          <div key={i} className="mb-2">
            <div className="font-serif">{r.name} <span className="text-[var(--bloom-ink-muted)] text-sm">· {r.role}</span></div>
            <div className="text-xs text-[var(--bloom-ink-muted)]">{r.email} {r.phone && `· ${r.phone}`}</div>
            {r.relationship && <div className="text-xs italic">{r.relationship}</div>}
          </div>
        ))}
      </DetailSection>

      <DetailSection title="Story">
        <Row label="Why join" v={d.why_join} multiline />
        <Row label="Vision" v={d.vision_statement} multiline />
        <Row label="Strengths" v={d.unique_strengths} multiline />
      </DetailSection>

      {photos.length > 0 && (
        <DetailSection title={`Photos (${photos.length})`}>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <a key={i} href={p.data_url} target="_blank" rel="noreferrer" className="block">
                <div className="aspect-square overflow-hidden border border-[var(--bloom-border)]">
                  <img src={p.data_url} alt={p.caption} className="w-full h-full object-cover" />
                </div>
                {p.caption && <div className="text-[10px] mt-1 text-[var(--bloom-ink-muted)] truncate">{p.caption}</div>}
              </a>
            ))}
          </div>
        </DetailSection>
      )}

      {(detail.signature_status?.total ?? 0) > 0 && (
        <DetailSection title={`Signatures (${detail.signature_status.signed_count}/${detail.signature_status.total})`}>
          {detail.signature_status.complete ? (
            <div className="inline-flex items-center gap-1 text-[var(--bloom-brand)] text-xs font-serif" data-testid="admin-sig-complete">
              <Check size={12} /> All required documents signed · activated {a.activated_at ? new Date(a.activated_at).toLocaleString() : ""}
            </div>
          ) : (
            <div className="text-xs italic text-[var(--bloom-ink-muted)]">
              Awaiting signatures
            </div>
          )}
          <div className="mt-3 space-y-2" data-testid="admin-sig-list">
            {(a.signatures || []).map((s, i) => (
              <div key={i} className="text-xs border-l-2 border-[var(--bloom-brand)] pl-3 py-1">
                <div className="font-serif text-sm">{s.document_title}</div>
                <div className="text-[var(--bloom-ink-muted)]">
                  Signed as <em>{s.signed_name}</em> · {new Date(s.signed_at).toLocaleString()}
                </div>
                <div className="text-[10px] text-[var(--bloom-ink-muted)] mt-0.5 font-mono">
                  ip {s.ip} · hash {s.document_hash?.slice(0, 12)}…
                </div>
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      <div className="border-t border-[var(--bloom-border)] mt-10 pt-6" data-testid="app-decision-panel">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle size={16} className="text-[var(--bloom-ink-muted)]" />
          <div className="bloom-label">Reviewer notes (sent to applicant)</div>
        </div>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal & applicant-facing notes…"
          className="bloom-input"
          style={{ resize: "vertical" }}
          data-testid="admin-review-notes"
        />

        {err && <div className="text-sm text-[var(--bloom-accent)] mt-3 font-serif italic">{err}</div>}

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={() => decide("under_review")} disabled={busy} className="bloom-btn bloom-btn-ghost" data-testid="action-under-review">
            <Send size={14} /> Mark Under Review
          </button>
          <button onClick={() => decide("more_info_requested")} disabled={busy} className="bloom-btn bloom-btn-outline" data-testid="action-more-info">
            Request More Info
          </button>
          <button onClick={() => decide("approved")} disabled={busy} className="bloom-btn bloom-btn-primary" style={{ background: "var(--bloom-brand)" }} data-testid="action-approve">
            <Check size={14} /> Approve
          </button>
          <button onClick={() => decide("rejected")} disabled={busy} className="bloom-btn bloom-btn-primary" style={{ background: "var(--bloom-accent)" }} data-testid="action-reject">
            <X size={14} /> Reject
          </button>
          <button
            onClick={doDelete}
            disabled={delBusy}
            className="bloom-btn bloom-btn-outline ml-auto"
            style={{ color: "var(--bloom-accent)", borderColor: "var(--bloom-accent)" }}
            data-testid="action-delete-application"
            title="Permanently delete this application (the grower account is kept)"
          >
            <Trash2 size={14} /> {delBusy ? "Deleting…" : "Delete application"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="border-t border-[var(--bloom-border)] mt-6 pt-5">
      <h4 className="font-serif text-xl mb-3">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, v, multiline }) {
  if (!v && v !== 0) return null;
  return (
    <div className={multiline ? "" : "flex gap-4 text-sm"}>
      <div className="bloom-label" style={{ minWidth: "120px" }}>{label}</div>
      <div className={`font-serif ${multiline ? "italic mt-1 leading-relaxed" : ""}`}>{String(v)}</div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className="border-l border-[var(--bloom-border)] pl-3">
      <div className="bloom-label mb-1">{label}</div>
      <div className={`font-serif text-2xl ${accent ? "text-[var(--bloom-brand)]" : ""}`}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] font-medium">{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}
